import AnalyticsIcon from '@mui/icons-material/Analytics';
import BusinessIcon from '@mui/icons-material/Business';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GroupsIcon from '@mui/icons-material/Groups';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import WorkIcon from '@mui/icons-material/Work';
import PaymentsIcon from '@mui/icons-material/Payments';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HubIcon from '@mui/icons-material/Hub';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import CampaignIcon from '@mui/icons-material/Campaign';
import ShareIcon from '@mui/icons-material/Share';
import InboxIcon from '@mui/icons-material/Inbox';
import PublishIcon from '@mui/icons-material/Publish';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import EventNoteIcon from '@mui/icons-material/EventNote';
import {
  HR_EMPLOYEE_SELF_SERVICE_NAV,
  HR_EMPLOYEE_SELF_SERVICE_PATH,
  HR_MANAGER_SELF_SERVICE_NAV,
  HR_MANAGER_SELF_SERVICE_PATH,
} from '@/config/hrSelfServicePortals';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BugReportIcon from '@mui/icons-material/BugReport';
import TimelineIcon from '@mui/icons-material/Timeline';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';

import type { NavSection } from '@/components/layout/Sidebar';
import type { ErpModule } from '@/api/platform.api';
import { FEATURE } from '@/core/features/featureCodes';
import { POS_OPERATIONAL_NAV, PROCUREMENT_OPERATIONAL_NAV } from '@/config/moduleRelated';

/** @deprecated Use `getAppWorkflowNav` via `appNavigation.ts` — retained for legacy imports only. */
export const orgNavSections: NavSection[] = [
  {
    id: 'pos',
    label: 'POS',
    module: 'POS',
    items: [
      { to: '/pos/dashboard', label: 'Dashboard', icon: DashboardIcon, end: true },
      { to: '/pos', label: 'Open POS', icon: PointOfSaleIcon, feature: FEATURE.POS_BILLING },
      { to: '/pos/held', label: 'Held bills', icon: ReceiptIcon, feature: FEATURE.POS_BILLING },
      { to: '/pos/shift', label: 'Shift', icon: ReceiptIcon, feature: FEATURE.POS_SHIFT_MANAGEMENT },
      { to: '/pos/returns', label: 'Returns', icon: ReceiptIcon, feature: FEATURE.POS_RETURNS },
      { to: '/pos/analytics', label: 'Analytics', icon: AnalyticsIcon, feature: FEATURE.POS_ANALYTICS },
      { to: '/pos/setup', label: 'POS setup', icon: SettingsIcon, feature: FEATURE.POS_BILLING },
      { to: '/pos/terminal-reports', label: 'POS reports', icon: AnalyticsIcon, feature: FEATURE.POS_BILLING },
      ...POS_OPERATIONAL_NAV,
    ],
  },
  {
    id: 'procurement',
    label: 'Procurement · Operations',
    module: 'PROCUREMENT',
    items: [
      { to: '/procurement', label: 'Dashboard', icon: RequestQuoteIcon, end: true, feature: FEATURE.PROCUREMENT_BASIC },
      ...PROCUREMENT_OPERATIONAL_NAV,
    ],
  },
  {
    id: 'crm',
    label: 'CRM · Operations',
    module: 'CRM',
    items: [
      { to: '/crm', label: 'Dashboard', icon: HubIcon, end: true, feature: FEATURE.CRM_BASIC },
      { to: '/crm/reports', label: 'Reports', icon: ReceiptIcon, feature: FEATURE.CRM_ANALYTICS },
      { to: '/crm/leads', label: 'Leads', icon: PeopleIcon, feature: FEATURE.CRM_BASIC },
      { to: '/crm/leads/board', label: 'Lead Pipeline', icon: WorkspacesIcon, feature: FEATURE.CRM_BASIC },
      { to: '/crm/contacts', label: 'Contacts', icon: PeopleIcon, feature: FEATURE.CRM_BASIC },
      { to: '/crm/companies', label: 'Companies', icon: BusinessIcon, feature: FEATURE.CRM_BASIC },
      { to: '/crm/deals', label: 'Deals', icon: WorkspacesIcon, feature: FEATURE.CRM_BASIC },
      { to: '/crm/pipelines', label: 'Pipelines', icon: WorkspacesIcon, feature: FEATURE.CRM_PIPELINE },
      { to: '/crm/followups', label: 'Follow-ups', icon: NotificationsActiveIcon, feature: FEATURE.CRM_BASIC },
      { to: '/crm/tasks', label: 'Tasks', icon: WorkIcon, feature: FEATURE.CRM_BASIC },
      { to: '/crm/campaigns', label: 'Campaigns', icon: CampaignIcon, feature: FEATURE.CRM_CAMPAIGNS },
      { to: '/crm/setup', label: 'Setup', icon: SettingsIcon, feature: FEATURE.CRM_BASIC },
    ],
  },
  {
    id: 'leadgen',
    label: 'Dashboard',
    module: 'LEAD_GEN',
    items: [
      { to: '/lead-gen', label: 'Dashboard', icon: HubIcon, end: true, feature: FEATURE.LEAD_GEN_BASIC },
      { to: '/lead-gen/forms', label: 'Forms', icon: CampaignIcon, feature: FEATURE.LEAD_GEN_BASIC },
      { to: '/lead-gen/landing-pages', label: 'Landing Pages', icon: PublishIcon, feature: FEATURE.LEAD_GEN_LANDING_PAGES },
      { to: '/lead-gen/campaigns', label: 'Campaigns', icon: CampaignIcon, feature: FEATURE.LEAD_GEN_CAMPAIGNS },
      { to: '/lead-gen/visitor-tracking', label: 'Visitor Tracking', icon: TimelineIcon, feature: FEATURE.LEAD_GEN_BASIC },
      { to: '/lead-gen/scoring', label: 'Lead Scoring', icon: RateReviewIcon, feature: FEATURE.LEAD_GEN_SCORING },
      { to: '/lead-gen/routing', label: 'Lead Routing', icon: HubIcon, feature: FEATURE.LEAD_GEN_ROUTING },
      { to: '/lead-gen/ab-tests', label: 'A/B Tests', icon: PublishIcon, feature: FEATURE.LEAD_GEN_LANDING_PAGES },
      { to: '/lead-gen/webhooks', label: 'Ad Webhooks', icon: HubIcon, feature: FEATURE.LEAD_GEN_BASIC },
      { to: '/lead-gen/enrichment', label: 'Enrichment', icon: RateReviewIcon, feature: FEATURE.LEAD_GEN_ABM },
      { to: '/lead-gen/sources', label: 'Lead Sources', icon: CampaignIcon, feature: FEATURE.LEAD_GEN_BASIC },
      { to: '/lead-gen/duplicates', label: 'Duplicate Review', icon: RateReviewIcon, feature: FEATURE.LEAD_GEN_BASIC },
      { to: '/lead-gen/reports', label: 'Reports & ROI', icon: AnalyticsIcon, feature: FEATURE.LEAD_GEN_ATTRIBUTION },
      { to: '/lead-gen/consent', label: 'GDPR & Consent', icon: SecurityIcon, feature: FEATURE.LEAD_GEN_BASIC },
    ],
  },
  {
    id: 'hr',
    label: 'HR · Operations',
    module: 'HR',
    items: [
      { to: '/hr', label: 'Dashboard', icon: GroupsIcon, end: true, feature: FEATURE.HR_BASIC },
      { to: '/hr/reports', label: 'Reports', icon: AnalyticsIcon, feature: FEATURE.HR_BASIC },
      { to: '/hr/employees', label: 'Employees', icon: GroupsIcon, feature: FEATURE.HR_BASIC },
      { to: '/hr/attendance', label: 'Time & attendance', icon: FactCheckIcon, feature: FEATURE.HR_ATTENDANCE },
      { to: '/hr/approvals', label: 'Approvals', icon: FactCheckIcon, feature: FEATURE.HR_BASIC },
      { to: '/hr/payroll', label: 'Payroll', icon: PaymentsIcon, feature: FEATURE.HR_BASIC },
      { to: '/hr/recruitment', label: 'Recruitment', icon: WorkIcon, feature: FEATURE.HR_BASIC },
      { to: '/hr/appraisal', label: 'Appraisal', icon: AnalyticsIcon, feature: FEATURE.HR_APPRAISAL },
      { to: '/hr/claims', label: 'Claims', icon: ReceiptIcon, feature: FEATURE.HR_BASIC },
      { to: '/hr/training', label: 'Training', icon: MenuBookIcon, feature: FEATURE.HR_BASIC },
      { to: HR_EMPLOYEE_SELF_SERVICE_PATH, label: HR_EMPLOYEE_SELF_SERVICE_NAV, icon: GroupsIcon, feature: FEATURE.HR_BASIC },
      { to: HR_MANAGER_SELF_SERVICE_PATH, label: HR_MANAGER_SELF_SERVICE_NAV, icon: GroupsIcon, feature: FEATURE.HR_BASIC },
      { to: '/hr/feedback360', label: '360 feedback', icon: AnalyticsIcon, feature: FEATURE.HR_APPRAISAL },
      { to: '/hr/setup', label: 'Setup', icon: AccountTreeIcon, feature: FEATURE.HR_BASIC },
    ],
  },
  {
    id: 'finance',
    label: 'Finance · Operations',
    module: 'FINANCE',
    items: [
      { to: '/finance', label: 'Dashboard', icon: AccountBalanceIcon, end: true, feature: FEATURE.FINANCE_BASIC },
      { to: '/finance/reports', label: 'Reports', icon: AnalyticsIcon, feature: FEATURE.FINANCE_ADVANCED_REPORTS },
      { to: '/finance/accounting', label: 'Accounting', icon: AccountTreeIcon, feature: FEATURE.FINANCE_ACCOUNTING },
      { to: '/finance/ledger', label: 'General ledger', icon: MenuBookIcon, feature: FEATURE.FINANCE_ACCOUNTING },
      { to: '/finance/invoices', label: 'Invoices', icon: ReceiptIcon, feature: FEATURE.FINANCE_BASIC },
      { to: '/finance/payments', label: 'Payments', icon: PaymentsIcon, feature: FEATURE.FINANCE_BASIC },
      { to: '/finance/banking', label: 'Banking', icon: AccountBalanceIcon, feature: FEATURE.FINANCE_BASIC },
      { to: '/finance/taxes', label: 'Taxes', icon: ReceiptIcon, feature: FEATURE.FINANCE_BASIC },
      { to: '/finance/expenses', label: 'Expenses', icon: PaymentsIcon, feature: FEATURE.FINANCE_BASIC },
      { to: '/finance/assets', label: 'Assets', icon: BusinessIcon, feature: FEATURE.FINANCE_ASSETS },
      { to: '/finance/setup', label: 'Setup', icon: SettingsIcon, feature: FEATURE.FINANCE_BASIC },
    ],
  },
  {
    id: 'projects',
    label: 'Projects · Operations',
    module: 'PROJECTS',
    items: [
      { to: '/projects', label: 'Dashboard', icon: AssignmentIcon, end: true, feature: FEATURE.PROJECTS_BASIC },
      { to: '/projects/reports', label: 'Reports', icon: AnalyticsIcon, feature: FEATURE.PROJECTS_ADVANCED_ANALYTICS },
      { to: '/projects/list', label: 'Projects', icon: AssignmentIcon, feature: FEATURE.PROJECTS_BASIC },
      { to: '/projects/tasks', label: 'Tasks', icon: FactCheckIcon, feature: FEATURE.PROJECTS_BASIC },
      { to: '/projects/boards', label: 'Boards', icon: ViewKanbanIcon, feature: FEATURE.PROJECTS_BASIC },
      { to: '/projects/sprints', label: 'Sprints', icon: TimelineIcon, feature: FEATURE.PROJECTS_SPRINTS },
      { to: '/projects/timesheets', label: 'Timesheets', icon: EventNoteIcon, feature: FEATURE.PROJECTS_TIMESHEETS },
      { to: '/projects/portfolio', label: 'Portfolio / PMO', icon: AnalyticsIcon, feature: FEATURE.PROJECTS_PORTFOLIO },
      { to: '/projects/issues', label: 'Issues', icon: BugReportIcon, feature: FEATURE.PROJECTS_BASIC },
      { to: '/projects/gantt', label: 'Gantt', icon: TimelineIcon, feature: FEATURE.PROJECTS_GANTT },
      { to: '/projects/resources', label: 'Resources', icon: GroupsIcon, feature: FEATURE.PROJECTS_BASIC },
      { to: '/projects/calendar', label: 'Calendar', icon: EventNoteIcon, feature: FEATURE.PROJECTS_BASIC },
      { to: '/projects/workload', label: 'Workload', icon: GroupsIcon, feature: FEATURE.PROJECTS_ADVANCED_ANALYTICS },
      { to: '/projects/automation', label: 'Automation', icon: CampaignIcon, feature: FEATURE.PROJECTS_AUTOMATION },
      { to: '/projects/setup', label: 'Setup', icon: SettingsIcon, feature: FEATURE.PROJECTS_BASIC },
    ],
  },
  {
    id: 'desk',
    label: 'Desk · Operations',
    module: 'DESK',
    items: [
      { to: '/desk', label: 'Dashboard', icon: SupportAgentIcon, end: true, feature: FEATURE.DESK_BASIC },
      { to: '/desk/reports', label: 'Reports', icon: ReceiptIcon, feature: FEATURE.DESK_ANALYTICS },
      { to: '/desk/tickets', label: 'Tickets', icon: SupportAgentIcon, feature: FEATURE.DESK_BASIC },
      { to: '/desk/inbox', label: 'Inbox', icon: InboxIcon, feature: FEATURE.DESK_BASIC },
      { to: '/desk/livechat', label: 'Live chat', icon: LiveHelpIcon, feature: FEATURE.DESK_LIVE_CHAT },
      { to: '/desk/projects', label: 'Projects', icon: WorkspacesIcon, feature: FEATURE.DESK_PROJECTS },
      { to: '/desk/boards', label: 'Boards', icon: ViewKanbanIcon, feature: FEATURE.DESK_PROJECTS },
      { to: '/desk/knowledgebase', label: 'Knowledge base', icon: MenuBookIcon, feature: FEATURE.DESK_BASIC },
      { to: '/desk/automation', label: 'Automation', icon: CampaignIcon, feature: FEATURE.DESK_AUTOMATION },
      { to: '/desk/canned-responses', label: 'Canned responses', icon: MenuBookIcon, feature: FEATURE.DESK_BASIC },
      { to: '/desk/setup', label: 'Setup', icon: SettingsIcon, feature: FEATURE.DESK_BASIC },
    ],
  },
  {
    id: 'social',
    label: 'Social · Operations',
    module: 'SOCIAL',
    items: [
      { to: '/social', label: 'Dashboard', icon: ShareIcon, end: true, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/inbox', label: 'Unified Inbox', icon: InboxIcon, feature: FEATURE.SOCIAL_INBOX },
      { to: '/social/publishing', label: 'Publishing', icon: PublishIcon, feature: FEATURE.SOCIAL_PUBLISHING },
      { to: '/social/calendar', label: 'Calendar', icon: EventNoteIcon, feature: FEATURE.SOCIAL_CALENDAR },
      { to: '/social/comments', label: 'Comments', icon: LiveHelpIcon, feature: FEATURE.SOCIAL_INBOX },
      { to: '/social/reviews', label: 'Reviews', icon: RateReviewIcon, feature: FEATURE.SOCIAL_REPUTATION },
      { to: '/social/analytics', label: 'Analytics', icon: AnalyticsIcon, feature: FEATURE.SOCIAL_ANALYTICS },
      { to: '/social/automation', label: 'Automation', icon: TimelineIcon, feature: FEATURE.SOCIAL_AUTOMATION },
      { to: '/social/crm-leads', label: 'CRM Leads', icon: PeopleIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/teams', label: 'Teams', icon: GroupsIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/connect', label: 'Connection Center', icon: HubIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/platforms', label: 'All platforms', icon: HubIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/webhooks', label: 'Webhooks', icon: HubIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/settings', label: 'Settings', icon: SettingsIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/platforms/facebook', label: 'Facebook', icon: ShareIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/platforms/instagram', label: 'Instagram', icon: ShareIcon, feature: FEATURE.SOCIAL_BASIC },
      { to: '/social/platforms/whatsapp', label: 'WhatsApp', icon: InboxIcon, feature: FEATURE.SOCIAL_INBOX },
      { to: '/social/platforms/linkedin', label: 'LinkedIn', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
      { to: '/social/platforms/x', label: 'X', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
      { to: '/social/platforms/youtube', label: 'YouTube', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
      { to: '/social/platforms/google_business', label: 'Google Business', icon: BusinessIcon, feature: FEATURE.SOCIAL_REPUTATION },
      { to: '/social/platforms/telegram', label: 'Telegram', icon: InboxIcon, feature: FEATURE.SOCIAL_INBOX },
      { to: '/social/platforms/threads', label: 'Threads', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
      { to: '/social/platforms/pinterest', label: 'Pinterest', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
      { to: '/social/platforms/tiktok', label: 'TikTok', icon: ShareIcon, feature: FEATURE.SOCIAL_PUBLISHING },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing · Operations',
    module: 'MARKETING',
    items: [
      { to: '/marketing', label: 'Dashboard', icon: HubIcon, end: true, feature: FEATURE.MARKETING_BASIC },
      { to: '/marketing/campaigns', label: 'Campaigns', icon: CampaignIcon, feature: FEATURE.MARKETING_EMAIL },
      { to: '/marketing/segments', label: 'Audiences', icon: PeopleIcon, feature: FEATURE.MARKETING_BASIC },
      { to: '/marketing/journeys', label: 'Journeys', icon: AccountTreeIcon, feature: FEATURE.MARKETING_JOURNEY },
      { to: '/marketing/templates', label: 'Templates', icon: PublishIcon, feature: FEATURE.MARKETING_EMAIL },
      { to: '/marketing/lifecycle', label: 'Lifecycle', icon: EventNoteIcon, feature: FEATURE.MARKETING_AUTOMATION },
      { to: '/marketing/reports', label: 'Reports', icon: AnalyticsIcon, feature: FEATURE.MARKETING_ATTRIBUTION },
      { to: '/marketing/webhooks', label: 'ESP webhooks', icon: HubIcon, feature: FEATURE.MARKETING_EMAIL },
    ],
  },
];

export const PRIMARY_ERP_MODULES: ErpModule[] = [
  'POS',
  'PROCUREMENT',
  'CRM',
  'LEAD_GEN',
  'MARKETING',
  'HR',
  'FINANCE',
  'PROJECTS',
  'DESK',
  'SOCIAL',
];
